import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import NotificationBell from "@/components/app/NotificationBell";
import BottomNav from "@/components/app/BottomNav";
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Truck,
  Boxes,
  Building2,
  LogOut,
  Plus,
  Settings,
  UserCog,
  Receipt,
  Menu,
  X,
  Users,
  CreditCard,
  FolderLock,
  Bell,
  Clock,
  GraduationCap,
} from "lucide-react";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const isAdmin = ["admin", "organization_admin", "manager"].includes(user?.role);
  const isManager = user?.role === "manager";
  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState({ orders: true, invoices: true, documents: true });

  useEffect(() => {
    if (!user) return;
    api.get("/user/modules")
      .then(r => setModules(r.data?.modules || { orders: true, invoices: true, documents: true }))
      .catch(() => {});
  }, [user]);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const chefNav = [
    { to: "/", label: t("dashboard"), icon: LayoutDashboard, end: true },
    { to: "/nieuwe-bestelling", label: t("new_order"), icon: Plus, module: "orders" },
    { to: "/bestellingen", label: t("orders"), icon: ShoppingCart, module: "orders" },
    { to: "/voorraad", label: t("inventory"), icon: Boxes, module: "orders" },
    { to: "/facturen", label: t("invoices"), icon: Receipt, module: "invoices" },
    { to: "/documenten", label: "Documenten", icon: FolderLock, module: "documents" },
    { to: "/training/haccp", label: "HACCP Training", icon: GraduationCap, module: "training" },
    { to: "/instellingen", label: t("settings"), icon: Settings },
  ];
  const adminNav = [
    { to: "/", label: t("dashboard"), icon: LayoutDashboard, end: true },
    { to: "/bestellingen", label: t("orders"), icon: ShoppingCart, module: "orders" },
    { to: "/producten", label: t("products"), icon: Package, module: "orders" },
    { to: "/leveranciers", label: t("suppliers"), icon: Truck, module: "orders" },
    { to: "/voorraad", label: t("inventory"), icon: Boxes, module: "orders" },
    { to: "/facturen", label: t("invoices"), icon: Receipt, module: "invoices" },
    { to: "/documenten", label: "Documenten", icon: FolderLock, module: "documents" },
    { to: "/restaurants", label: t("restaurants"), icon: Building2 },
    { to: "/team", label: "Team", icon: Users },
    { to: "/shiftregistratie", label: "Shiftregistratie", icon: Clock, module: "shifts" },
    { to: "/training/haccp", label: "HACCP Training", icon: GraduationCap, module: "training" },
    { to: "/instellingen", label: t("settings"), icon: Settings },
  ];

  const navItems = (isAdmin ? adminNav : chefNav)
    .filter(i => !i.module || modules[i.module])
    .filter(i => !(isManager && i.to === "/restaurants"));

  const SidebarContent = (
    <>
      <div className="px-4 py-5 border-b border-zinc-100 flex items-center justify-between lg:justify-center">
        <img src="/stockly-logo-tight.png" alt="Stockly" className="w-[190px] lg:w-[236px] h-auto" data-testid="sidebar-logo" />
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="lg:hidden w-9 h-9 inline-flex items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100"
          data-testid="sidebar-close-button"
          aria-label="Sluiten"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        <div className="overline px-3 pb-2">{t("navigation")}</div>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            data-testid={`nav-${item.to.replace(/\//g, "-")}`}
          >
            <item.icon size={17} strokeWidth={1.7} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-zinc-100 space-y-1">
        <NavLink to="/profiel" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid="nav-profile">
          <div className="w-7 h-7 rounded-full bg-[#1B2D5C] text-white flex items-center justify-center text-xs font-semibold overflow-hidden shrink-0">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              user?.name?.charAt(0)?.toUpperCase() || "?"
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">
              {isManager ? "Manager" : user?.role === "bediening" ? "Bediening" : (isAdmin ? t("admin_role") : t("chef_role"))}
            </div>
          </div>
          <UserCog size={14} strokeWidth={1.7} className="text-zinc-400" />
        </NavLink>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full nav-link justify-start"
          data-testid="logout-button"
        >
          <LogOut size={17} strokeWidth={1.7} />
          {t("logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen lg:flex bg-[#FAFAFA]" data-testid="app-layout">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-white border-b border-zinc-200 px-3 h-14" data-testid="mobile-topbar" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-10 h-10 inline-flex items-center justify-center rounded-md text-zinc-700 hover:bg-zinc-100"
          data-testid="mobile-menu-button"
          aria-label="Menu openen"
        >
          <Menu size={22} />
        </button>
        <img src="/stockly-logo-tight.png" alt="Stockly" className="h-9 w-auto" data-testid="topbar-logo" />
        <NotificationBell />
      </header>

      {/* Desktop floating notification bell */}
      <div className="hidden lg:block fixed top-4 right-6 z-30">
        <div className="bg-white border border-zinc-200 rounded-md shadow-sm">
          <NotificationBell />
        </div>
      </div>

      {/* Mobile drawer overlay */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar — desktop static, mobile drawer */}
      <aside
        className={`fixed lg:static top-0 left-0 h-full lg:h-auto z-50 w-[280px] max-w-[85vw] shrink-0 bg-white border-r border-zinc-200 flex flex-col transform transition-transform duration-200 ease-out ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        {SidebarContent}
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden pb-16 lg:pb-0" data-testid="main-content">
        <Outlet />
      </main>
      <BottomNav isAdmin={isAdmin} modules={modules} onMore={() => setOpen(true)} />
    </div>
  );
}
